import React, {useCallback, useMemo, useRef, useState} from 'react';
import type {ECharts, EChartsReactRef} from '@components/EChartsReact';
import ReactECharts from '@components/EChartsReact';
import {useLocalizedFormat} from '@deezer/next/contexts/localization';
import {useTranslation} from 'next-i18next';
import type {ResponsiveValue} from '@tempo/core';
import {Box, Button, useBreakpointValue} from '@tempo/core';
import {ArrowRightIcon} from '@tempo/icons';
import type {EventListeners} from '@components/EChartsReact/types';
import type {BarChartSeries, DrilldownDataPoint} from './types';
import {useToken} from '@tempo/system';

type BarChartProps = {
	series: BarChartSeries[];
	drilldown?: DrilldownDataPoint[];
	orientation?: ResponsiveValue<'vertical' | 'horizontal'>;
};

const BarChart = ({
	series,
	drilldown = [],
	orientation: responsiveOrientation = 'vertical',
}: BarChartProps) => {
	const orientation =
		useBreakpointValue(
			typeof responsiveOrientation === 'string'
				? {base: responsiveOrientation}
				: responsiveOrientation,
		) ??
		(typeof responsiveOrientation === 'string'
			? responsiveOrientation
			: 'vertical');

	const primaryTextColor = useToken('colors', 'text.neutral.primary.default');

	const secondaryTextColor = useToken(
		'colors',
		'text.neutral.secondary.default',
	);

	const textColor = useToken('colors', 'text.neutral.primary.default');

	const subtitleColor = useToken('colors', 'text.neutral.secondary.default');

	const highlightColor = useToken('colors', 'text.accent.primary.default');

	const echartsRef = useRef<EChartsReactRef>(null);

	const {t} = useTranslation('d4c');
	const {formatNumber} = useLocalizedFormat();

	const [drilledGroupId, setDrilledGroupId] = useState<string | null>(null);
	const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);

	const rootData = useMemo(() => series[0]?.data ?? [], [series]);

	const isDrilledDown = drilledGroupId !== null;
	const isMultiRowChart = rootData.length > 1;
	const showLegend = !isDrilledDown && series.length > 1;
	const isHorizontal = orientation === 'horizontal';

	const activeDrilldown = useMemo(
		() =>
			drilledGroupId
				? drilldown.filter(item => item.groupId === drilledGroupId)
				: [],
		[drilldown, drilledGroupId],
	);

	const rootAxisLabelRich = useMemo(() => {
		return rootData.reduce<Record<string, any>>((acc, item) => {
			if (!item.groupId) {
				return acc;
			}

			const isHovered = hoveredGroupId === item.groupId;

			acc[`${item.groupId}_title`] = {
				color: isHovered ? highlightColor : textColor,
			};

			acc[`${item.groupId}_subtitle`] = {
				color: isHovered ? highlightColor : subtitleColor,
				fontStyle: 'italic',
				padding: [6, 0, 0, 0],
			};

			return acc;
		}, {});
	}, [highlightColor, hoveredGroupId, rootData, subtitleColor, textColor]);

	const rootCategoryAxis = useMemo(
		() => ({
			id: 'root-category-axis',
			type: 'category' as const,
			triggerEvent: true,
			inverse: isHorizontal,

			axisLine: {
				show: false,
			},

			axisTick: {
				show: false,
			},

			axisLabel: {
				show: isMultiRowChart,

				formatter: (value: string, index: number) => {
					const groupId = rootData[index]?.groupId;

					if (groupId) {
						return (
							`{${groupId}_title|${value.toUpperCase()}}` +
							'\n' +
							`{${groupId}_subtitle|${t(
								'analyticshome_action_digdeeper_d4c-web',
							)}}`
						);
					}

					return `{title|${value.toUpperCase()}}`;
				},

				rich: {
					title: {
						color: textColor,
					},
					...rootAxisLabelRich,
				},
			},

			data: rootData.map(item => item.name),
		}),
		[isHorizontal, isMultiRowChart, rootAxisLabelRich, rootData, t, textColor],
	);

	const drilldownCategoryAxis = useMemo(
		() => ({
			id: 'drilldown-category-axis',
			type: 'category' as const,
			triggerEvent: false,
			inverse: isHorizontal,

			axisLine: {
				show: false,
			},

			axisTick: {
				show: false,
			},

			axisLabel: {
				formatter: (value: string) => `{title|${value.toUpperCase()}}`,

				rich: {
					title: {
						color: textColor,
					},
				},
			},

			data: activeDrilldown[0]?.data.map(item => item[0]) ?? [],
		}),
		[activeDrilldown, isHorizontal, textColor],
	);

	const valueAxis = useMemo(
		() => ({
			id: 'value-axis',
			type: 'value' as const,
			show: false,

			axisLine: {
				show: false,
			},

			axisTick: {
				show: false,
			},

			axisLabel: {
				show: false,
			},

			splitLine: {
				show: false,
			},
		}),
		[],
	);

	const chartSeries = useMemo(() => {
		if (isDrilledDown) {
			return activeDrilldown.map(({groupId, data, name}) => ({
				id: `drilldown-${groupId}-${name}`,
				name,
				type: 'bar' as const,
				dataGroupId: groupId,
				colorBy: 'series' as const,
				cursor: 'default',
				clip: false,

				data: data.map(([label, value]) => ({
					name: label,
					value,
				})),

				label: {
					show: true,
					position: isHorizontal ? 'right' : 'top',
					distance: 8,
					color: textColor,

					formatter: ({data}: {data: {value: number}}) =>
						formatNumber(data.value / 100, {
							style: 'percent',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						}),
				},

				labelLayout: {
					hideOverlap: true,
					moveOverlap: 'shiftY',
				},

				itemStyle: {
					borderRadius: 4,
				},

				barMaxWidth: isHorizontal ? 20 : 48,

				universalTransition: {
					enabled: true,
					divideShape: 'clone' as const,
				},
			}));
		}

		return series.map(s => ({
			id: `root-${s.name}`,
			name: s.name,
			type: 'bar' as const,

			colorBy: series.length > 1 ? ('series' as const) : ('data' as const),

			clip: false,

			data: s.data.map(item => ({
				...item,
				value: item.value,
				groupId: item.groupId,
				name: item.name,
			})),

			...(s.data.length > 1
				? {}
				: {
						tooltip: {
							valueFormatter: () =>
								formatNumber(s.data[0].percent / 100, {
									style: 'percent',
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}),
						},
					}),

			label: {
				show: s.data.length > 1,
				position: isHorizontal ? 'right' : 'top',
				distance: 8,
				color: textColor,

				formatter: ({data}: {data: {percent: number}}) =>
					formatNumber(data.percent / 100, {
						style: 'percent',
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					}),
			},

			labelLayout: {
				hideOverlap: true,
				moveOverlap: isHorizontal ? 'shiftY' : 'shiftX',
			},

			itemStyle: {
				borderRadius: 4,
			},

			barMaxWidth: isHorizontal ? 20 : 48,

			universalTransition: {
				enabled: true,
				divideShape: 'clone' as const,
			},
		}));
	}, [
		activeDrilldown,
		formatNumber,
		isDrilledDown,
		isHorizontal,
		series,
		textColor,
	]);

	const gridTop = isDrilledDown ? 48 : showLegend ? 40 : 0;

	const gridRight = isHorizontal
		? 64
		: isMultiRowChart || isDrilledDown
			? 24
			: 8;

	const gridBottom = isDrilledDown
		? isHorizontal
			? 16
			: 40
		: showLegend
			? isHorizontal
				? 50
				: 72
			: isMultiRowChart
				? isHorizontal
					? 20
					: 40
				: 24;

	const options = useMemo(
		() => ({
			/**
			 * ECharts 6 makes rich text inherit styles
			 * from the parent plain label.
			 *
			 * Keep the ECharts 5 behaviour.
			 */
			richInheritPlainLabel: false,

			animationDurationUpdate: 300,

			tooltip: {
				renderMode: 'richText' as const,

				axisPointer: {
					type: 'shadow' as const,
				},

				...(!isDrilledDown && isMultiRowChart
					? {
							trigger: 'axis' as const,

							valueFormatter: (value: number) =>
								formatNumber(value / 100, {
									style: 'percent',
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								}),
						}
					: {
							trigger: 'item' as const,
						}),
			},

			legend: {
				show: showLegend,
				type: 'scroll' as const,
				left: 'center',
				bottom: 0,
				icon: 'circle',

				...(!showLegend || isMultiRowChart
					? {}
					: {
							formatter: (name: string) => {
								const serie = series.find(s => s.name === name);

								if (!serie) {
									return name;
								}

								const {percent, value} = serie.data[0];

								return [
									`{title|${name}}`,

									`{percentage|${formatNumber(percent / 100, {
										style: 'percent',
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})}}`,

									`{value|${t('analyticsplaylist_text_Xnumberstreams_d4c-web', {
										count: value,
										sprintf: [formatNumber(value)],
									})}}`,
								].join('\n');
							},

							textStyle: {
								rich: {
									title: {
										fontSize: 14,
										fontWeight: 600,
										color: secondaryTextColor,
										padding: [0, 0, 4, 0],
									},

									percentage: {
										fontSize: 20,
										color: primaryTextColor,
										height: 24,
										fontWeight: 700,
									},

									value: {
										fontSize: 12,
										color: secondaryTextColor,
										padding: [0, 0, 4, 0],
									},
								},
							},
						}),
			},

			grid: {
				top: gridTop,
				left: 0,
				right: gridRight,
				bottom: gridBottom,

				/**
				 * Intentionally kept for ECharts 5 layout compatibility.
				 * The full ECharts 6 bundle registers LegacyGridContainLabel.
				 */
				containLabel: true,
			},

			xAxis: isHorizontal
				? valueAxis
				: isDrilledDown
					? drilldownCategoryAxis
					: rootCategoryAxis,

			yAxis: isHorizontal
				? isDrilledDown
					? drilldownCategoryAxis
					: rootCategoryAxis
				: valueAxis,

			series: chartSeries,
		}),
		[
			chartSeries,
			drilldownCategoryAxis,
			formatNumber,
			gridBottom,
			gridRight,
			gridTop,
			isDrilledDown,
			isHorizontal,
			isMultiRowChart,
			primaryTextColor,
			rootCategoryAxis,
			secondaryTextColor,
			series,
			showLegend,
			t,
			valueAxis,
		],
	);

	const onSeeFewerClick = useCallback(() => {
		const echarts = echartsRef.current?.getEchartsInstance();

		echarts?.dispatchAction({
			type: 'hideTip',
		});

		setHoveredGroupId(null);
		setDrilledGroupId(null);
	}, []);

	const eventHandlers = useMemo<EventListeners>(
		() => ({
			click: (event: any, echarts?: ECharts) => {
				if (isDrilledDown) {
					return;
				}

				let groupId: string | undefined;

				if (event.targetType === 'axisLabel') {
					groupId = rootData[event.dataIndex]?.groupId;
				} else if (event.data) {
					groupId = event.data.groupId;
				}

				if (!groupId) {
					return;
				}

				const hasDrilldown = drilldown.some(item => item.groupId === groupId);

				if (!hasDrilldown) {
					return;
				}

				echarts?.dispatchAction({
					type: 'hideTip',
				});

				setHoveredGroupId(null);
				setDrilledGroupId(groupId);
			},

			mouseover: (event: any) => {
				if (isDrilledDown) {
					return;
				}

				let groupId: string | undefined;

				if (event.targetType === 'axisLabel') {
					groupId = rootData[event.dataIndex]?.groupId;
				} else if (event.data) {
					groupId = event.data.groupId;
				}

				setHoveredGroupId(groupId ?? null);
			},

			mouseout: () => {
				if (isDrilledDown) {
					return;
				}

				setHoveredGroupId(null);
			},
		}),
		[drilldown, isDrilledDown, rootData],
	);

	return (
		<Box position="relative">
			<ReactECharts
				ref={echartsRef}
				option={options}
				notMerge={false}
				replaceMerge={['xAxis', 'yAxis', 'series', 'legend']}
				height={400}
				mt={4}
				onEvents={eventHandlers}
			/>

			{isDrilledDown && (
				<Button
					size="small"
					variant="outline"
					position="absolute"
					top={0}
					right={0}
					leftIcon={<ArrowRightIcon transform="scaleX(-1)" />}
					onClick={onSeeFewerClick}
				>
					{t('generic_action_seefewer_d4c-web')}
				</Button>
			)}
		</Box>
	);
};

export default BarChart;
